import React, { useEffect } from 'react';
import { Stack } from 'react-bootstrap';
import {
  FormWrapper,
  LoadingAnimation,
  Navigation,
  useWizardContext,
} from '@odmonorepo/core-components';
import {
  getCheckoutState,
  getIdentityQuestions,
  postIdentityAnswers,
} from 'merchant-api/checkout';
import { FormattedMessage } from 'react-intl';
import { IdentityForm } from './IdentityForm';
import { useFormContext } from 'react-hook-form';
import { IdentityQuestion, IdentityStatus } from 'merchant-api/types';
import * as yup from 'yup';
import { toIdentityAnswers, getNextStepAfterIdentity } from './helper';

export const IdentityWrapper = () => {
  const { goToStep } = useWizardContext();
  const { identityData, isLoadingIdentityQuestions, isErrorIdentityQuestions } =
  getIdentityQuestions();
  const { checkoutData, isErrorCheckoutState, isLoadingCheckoutState } = getCheckoutState();
  const buildIdentitySchema = (questions: IdentityQuestion[]) =>
    yup.object(
      Object.fromEntries(
        questions.map((q) => [q.type.replace(/\./g, '_'), yup.string().required('Select an answer.')])
      )
    );

  if (isErrorIdentityQuestions || isErrorCheckoutState) goToStep('IDENTITY_FAILED');

  if (isLoadingIdentityQuestions || isLoadingCheckoutState || !identityData) {
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );
  }

  const schema = buildIdentitySchema(identityData?.questions ?? []);

  return (
    <FormWrapper schema={schema}>
      <Identity identityData={identityData} checkoutData={checkoutData}/>
    </FormWrapper>
  );
}

const Identity = ({ identityData, checkoutData }) => {

  const { sendIdentityAnswers, isLoadingIdentityAnswers } = postIdentityAnswers();
  const { goToStep, stepOrder } = useWizardContext();
  const { getValues } = useFormContext();
  const onSubmit = () => {
    sendIdentityAnswers(toIdentityAnswers(getValues(), identityData.idNumber))
      .catch(() => {
        goToStep('IDENTITY_FAILED');
      });
  };

  useEffect(() => {
    switch (checkoutData.identityVerificationStatus) {
      case IdentityStatus.PASS:
        const nextStep = getNextStepAfterIdentity({
          stepOrder,
          checkoutData,
        });
        goToStep(nextStep);
        break;
      case IdentityStatus.CHALLENGE:
        goToStep('IDENTITY_CHALLENGE');
        break;
      case IdentityStatus.FAIL:
        goToStep('IDENTITY_FAILED');
        break;
      default:
        break;
    }
  }, [checkoutData.identityVerificationStatus]);

   if (isLoadingIdentityAnswers) {
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );
  }


  return (
    <Stack
      gap={4}
      style={{ maxWidth: '600px', width: '100%' }}
      className="mx-auto"
    >
      <h3 className="text-center">
        <FormattedMessage id="identity.title" />
      </h3>
      <IdentityForm identityData={identityData} />
      <Navigation continueOnly handleNext={onSubmit} continueText={stepOrder.includes('REQUIRED_FIRST_DRAW') ? "Continue to First Draw" : "Continue to Agreement"}/>
    </Stack>
  );
};

export default IdentityWrapper;
